import {RootStore} from "../RootStore";
import {makeAutoObservable, reaction, runInAction} from "mobx";
import agent from "../../api/Agent";
import Agent from "../../api/Agent";
import {ISemiAnnualReport} from "../../models/SemiAnnualReport/SemiAnnualReport";
import {toast} from "react-toastify";
import {IAttachment} from "../../models/generic/Attachment";

export default class SemiAnnualReportStore {
    rootStore: RootStore

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore
        makeAutoObservable(this)

        reaction(
            () => this.predicate.keys(),
            () => {
                this.page = 0
                this.semiAnnualReportsRegistry.clear()
                this.loadSemiAnnualReports()
            }
        )
    }

    semiAnnualReportsRegistry = new Map()
    semiAnnualReport: ISemiAnnualReport | null = null
    limit = 5
    page = 0
    predicate = new Map()
    semiAnnualReportsCount = 0;

    setPredicate = (predicate?: string, value?: any) => {
        if (!predicate || predicate === '') {
            this.predicate.clear()
        } else {
            this.predicate.delete(predicate)
            if (!!value)
                this.predicate.set(predicate, value instanceof Date ? new Date(value).toISOString() : value)
        }
    }

    get axiosParams() {
        const params = new URLSearchParams()
        params.append('limit', String(this.limit))
        params.append('offset', `${this.page ? this.page * this.limit : 0}`)
        this.predicate.forEach((value, key) => {
            if (!(value === '' || value === undefined || value === 'undefined') && typeof value !== 'object'){
                params.append(key, value)
            } else if (typeof value === 'object') {
                value?.forEach(val => params.append(key, val))
            }
        })
        return params
    }

    get pageCount() {
        return Math.ceil(this.semiAnnualReportsCount / this.limit)
    }

    get semiAnnualReportsList() {
        // @ts-ignore
        return Array.from(this.semiAnnualReportsRegistry.values()).sort((a,b) => new Date(b.updatedOn) - new Date(a.updatedOn))
    }

    clearSemiAnnualReports = async () => {
        this.semiAnnualReportsRegistry.clear()
    }
    setPage = (page: number) => {
        this.page = page
    }
    setLimit = (limit: number) => {
        this.limit = limit
    }

    setCurrentSemiAnnualReport = (semiAnnualReport?) => {
        this.semiAnnualReport = semiAnnualReport
    }

    handleChangePage = (newPage: number, newRow: number) => {
        //sets page, we do -1 because the offset will be multiplied against
        this.setPage(newPage - 1)
        //sets the row limit
        this.setLimit(newRow)
        //clears all records in the registry so that next page didnt load the prev records
        this.semiAnnualReportsRegistry.clear()
        this.loadSemiAnnualReports()
    }

    loadSemiAnnualReports = async () => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            const SemiAnnualReportEnvelope = await agent.SemiAnnualReport.list(this.axiosParams)
            let {modelsDto, modelCount} = SemiAnnualReportEnvelope

            runInAction(() => {
                modelsDto.forEach((semiAnnualReport) => {
                    this.semiAnnualReportsRegistry.set(semiAnnualReport.id, semiAnnualReport)
                })
                this.semiAnnualReportsCount = modelCount
            })

        } catch (error) {
            this.rootStore.errorStore.addError(error);
        } finally {
            this.rootStore.commonStore.setIsLoading(loadingId)
        }
    }

    getSemiAnnualReportById = async (id) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        let semiAnnualReport;
        try {
            semiAnnualReport = await agent.SemiAnnualReport.byId(id);
        } catch (error) {
            this.rootStore.errorStore.addError(error);
        } finally {
            this.rootStore.commonStore.setIsLoading(loadingId)
        }

        return semiAnnualReport
    }

    reviewedSemiAnnualReport = async (semiAnnualReport: ISemiAnnualReport, submit?: Partial<IAttachment>) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            const tmpSemiAnnualReport = await Agent.SemiAnnualReport.review(semiAnnualReport.id ?? '', submit);
            this.semiAnnualReportsRegistry.set(semiAnnualReport.id, tmpSemiAnnualReport);
            toast.success('Saved')
            return tmpSemiAnnualReport;
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            this.rootStore.commonStore.setIsLoading(loadingId)
        }
    }

    approveSemiAnnualReport = async (semiAnnualReport: ISemiAnnualReport) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            const tmpSemiAnnualReport = await Agent.SemiAnnualReport.approve(semiAnnualReport.id ?? '');
            this.semiAnnualReportsRegistry.set(tmpSemiAnnualReport.id, tmpSemiAnnualReport);
            toast.success('Saved')
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            this.rootStore.commonStore.setIsLoading(loadingId)
        }
    }
    rejectSemiAnnualReport = async (semiAnnualReport: ISemiAnnualReport) => {
        let loadingId = this.rootStore.commonStore.setIsLoading()
        try {
            const tmpSemiAnnualReport = await Agent.SemiAnnualReport.reject(semiAnnualReport.id ?? '');
            this.semiAnnualReportsRegistry.delete(tmpSemiAnnualReport.id);
            toast.success('Saved')
        } catch (error) {
            this.rootStore.errorStore.addError(error)
        } finally {
            this.rootStore.commonStore.setIsLoading(loadingId)
        }
    }
}
